import { GridRowId } from '@mui/x-data-grid-pro'
import async from 'async'
import { parse } from 'date-fns'
import { v4 } from 'uuid'
import { Country } from 'components/common/LocaleContext'
import { AttendeeFormData } from 'domain/models/AttendeeFormData/AttendeeFormData'
import {
  CsvImportAttendee,
  ImportAttendee
} from 'domain/models/Import/ImportAttendee'
import { ImportAttendeeRow } from 'domain/models/Import/ImportAttendeeRow'
import { EditionId } from 'domain/models/ValueObject/EntityIds'
import { ImportAttendeeRepository } from 'domain/repositories/ImportAttendeeRepository'
import { isNonEmptyArray } from 'helpers/array'
import { liveImportAttendeeFrontReadService } from 'live/liveBrowserInstances'
import { defaultAttendeeFormValues } from 'services/attendees/AttendeeFormService'
import {
  getFromLocalStorage,
  setInLocalStorage
} from 'services/localStorage/getOrSetLocalStorage'
import {
  convertAddress,
  convertCountry,
  convertFederation,
  convertGender
} from './ImportAttendeeConvertHelper'
import { ImportAttendeeSchema } from './ImportAttendeeSchema'

const IMPORT_ATTENDEE_KEY = 'importAttendee'

const isEmptyRow = ({ formData, raceId }: ImportAttendeeRow) => {
  const { particulars, misc } = defaultAttendeeFormValues
  const { particulars: p, misc: m, document: d, organizer: o } = formData

  return (
    !raceId &&
    p.email === particulars.email &&
    p.firstName === particulars.firstName &&
    p.lastName === particulars.lastName &&
    p.dateOfBirth === particulars.dateOfBirth &&
    p.gender === particulars.gender &&
    p.nationality === particulars.nationality &&
    p.address === particulars.address &&
    m?.clubOrAssociation === misc?.clubOrAssociation &&
    !d?.documentNumber &&
    !d?.federationName &&
    !o?.paymentStatus
  )
}

export class LiveImportAttendeeRepository implements ImportAttendeeRepository {
  async create(editionId: EditionId): Promise<ImportAttendeeRow[]> {
    const importAttendee: ImportAttendee = {
      rows: [
        {
          id: v4(),
          formData: defaultAttendeeFormValues,
          options: {}
        }
      ]
    }
    setInLocalStorage(
      `${IMPORT_ATTENDEE_KEY}_${editionId}`,
      JSON.stringify(importAttendee)
    )
    return importAttendee.rows
  }

  async addRow(row: ImportAttendeeRow, editionId: EditionId): Promise<void> {
    const localStorageImportAttendee = getFromLocalStorage(
      `${IMPORT_ATTENDEE_KEY}_${editionId}`
    )
    if (localStorageImportAttendee) {
      const importAttendee = ImportAttendeeSchema.parse(
        JSON.parse(localStorageImportAttendee)
      )
      importAttendee.rows.push(row)
      setInLocalStorage(
        `${IMPORT_ATTENDEE_KEY}_${editionId}`,
        JSON.stringify(importAttendee)
      )
    }
  }

  async saveRow(
    newRow: ImportAttendeeRow,
    editionId: EditionId
  ): Promise<void> {
    const localStorageImportAttendee = getFromLocalStorage(
      `${IMPORT_ATTENDEE_KEY}_${editionId}`
    )
    if (localStorageImportAttendee) {
      const importAttendee = ImportAttendeeSchema.parse(
        JSON.parse(localStorageImportAttendee)
      )
      importAttendee.rows = importAttendee.rows.map((row) =>
        row.id === newRow.id ? newRow : row
      )
      setInLocalStorage(
        `${IMPORT_ATTENDEE_KEY}_${editionId}`,
        JSON.stringify(importAttendee)
      )
    }
  }
  async deleteRows(
    selectedRowIds: GridRowId[],
    editionId: EditionId
  ): Promise<void> {
    const localStorageImportAttendee = getFromLocalStorage(
      `${IMPORT_ATTENDEE_KEY}_${editionId}`
    )
    if (localStorageImportAttendee) {
      const importAttendee = ImportAttendeeSchema.parse(
        JSON.parse(localStorageImportAttendee)
      )
      importAttendee.rows = importAttendee.rows.filter(
        (row) => !selectedRowIds.includes(row.id)
      )
      setInLocalStorage(
        `${IMPORT_ATTENDEE_KEY}_${editionId}`,
        JSON.stringify(importAttendee)
      )
    }
  }
  async importAttendees(
    rowsToImport: CsvImportAttendee[],
    editionId: EditionId,
    countries: Country[]
  ) {
    const importAttendees = await async.mapLimit(
      rowsToImport,
      10,
      async (row: CsvImportAttendee): Promise<AttendeeFormData> => ({
        particulars: {
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          dateOfBirth: row.birthDate
            ? parse(row.birthDate, 'dd/MM/yyyy', new Date())
            : undefined,
          gender: convertGender(row.gender),
          phone: row.phoneNumber,
          nationality: convertCountry(row.nationality, countries),
          address: row.streetNumberAndStreetName
            ? await convertAddress(row.streetNumberAndStreetName)
            : undefined
        },
        document: {
          federationName: convertFederation(row.federationName),
          documentNumber: row.licenceNumber
        },
        misc: {
          clubOrAssociation: row.clubOrAssociation
        }
      })
    )

    await this.deleteDefaultRow(editionId)
    const importAttendee =
      await liveImportAttendeeFrontReadService.getImportAttendee(editionId)
    const importAttendeeRows: ImportAttendeeRow[] = importAttendees.map(
      (formData) => ({
        formData,
        raceId: undefined,
        options: {},
        id: v4()
      })
    )

    importAttendeeRows.forEach((row) => this.addRow(row, editionId))
    return [...(importAttendee?.rows ?? []), ...importAttendeeRows]
  }

  async deleteDefaultRow(editionId: EditionId) {
    const importAttendee =
      await liveImportAttendeeFrontReadService.getImportAttendee(editionId)
    const importAttendeeRow = importAttendee?.rows
    if (
      importAttendeeRow &&
      isNonEmptyArray(importAttendeeRow) &&
      isEmptyRow(importAttendeeRow[0])
    ) {
      await this.deleteRows([importAttendeeRow[0].id], editionId)
    }
  }
}
